import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import DatePicker from '../../ui/DatePicker';
import AddressAutocomplete from '../../ui/AddressAutocomplete';
import { toast } from '../../ui/Toast';
import { isAlreadyGone } from '../../../api/client';
import { Button } from '../../ui/Button';
import { hotelsApi } from '../../../api/hotels.api';
import { localInputToISO, isoToLocalInput } from '../../../utils/dateTime';
import { useDocumentExtraction, uploadStagedFiles } from '../../../hooks/useDocumentExtraction';
import type { Hotel, HotelDocument } from '../../../types';
import { DocumentAttachments } from './DocumentAttachments';
import { ReminderField } from './ReminderField';
import { ExtractingPlaceholder } from './ExtractingPlaceholder';
import { FIELD_LABEL } from './documentFiles';

interface Props {
  tripId: string;
  tripStartDate?: string;
  initial?: Hotel;
  initialFiles?: File[];
  onSaved: (h: Hotel) => void;
  onCancel: () => void;
}

export function HotelForm({ tripId, tripStartDate, initial, initialFiles, onSaved, onCancel }: Props) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const err = (bad: boolean) => bad ? ' input-error' : '';

  const [existingDocs, setExistingDocs] = useState<HotelDocument[]>(initial?.documents ?? []);
  const [docToDelete, setDocToDelete] = useState<HotelDocument | null>(null);
  const [v, setV] = useState({
    name: initial?.name ?? '',
    address: initial?.address ?? '',
    checkIn: initial?.checkIn ? isoToLocalInput(initial.checkIn) : '',
    checkOut: initial?.checkOut ? isoToLocalInput(initial.checkOut) : '',
    reminderMinutes: (initial?.reminderMinutes ?? null) as number | null,
    notes: initial?.notes ?? '',
  });

  const files = useDocumentExtraction({
    initialFiles,
    extract: fs => hotelsApi.extract(tripId, fs),
    onExtracted: extracted => setV(x => ({
      name:     extracted.name || x.name,
      address:  extracted.address ?? x.address,
      checkIn:  extracted.checkIn.slice(0, 16),
      checkOut: extracted.checkOut.slice(0, 16),
      reminderMinutes: x.reminderMinutes,
      notes: extracted.bookingRef
        ? [x.notes, `PNR: ${extracted.bookingRef}`].filter(Boolean).join('\n')
        : x.notes,
    })),
  });

  async function removeExistingDoc(doc: HotelDocument) {
    if (!initial) return;
    try {
      await hotelsApi.deleteDocument(tripId, initial.id, doc.id);
    } catch (e) {
      if (!isAlreadyGone(e)) {
        toast(t('bookings.toast.uploadFailed'), 'error');
        setDocToDelete(null);
        return;
      }
    }
    setExistingDocs(prev => prev.filter(d => d.id !== doc.id));
    toast(t('bookings.toast.fileDeleted'), 'success');
    setDocToDelete(null);
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitted(true);
    if (!v.name.trim() || !v.checkIn || !v.checkOut) return;

    setBusy(true);
    try {
      const payload = {
        ...v,
        checkIn: localInputToISO(v.checkIn),
        checkOut: localInputToISO(v.checkOut),
        reminderMinutes: v.reminderMinutes ?? undefined,
      };
      const result = initial
        ? await hotelsApi.update(tripId, initial.id, payload)
        : await hotelsApi.create(tripId, payload);

      const docs = await uploadStagedFiles(
        files.stagedFiles,
        file => hotelsApi.uploadDocument(tripId, result.id, file),
        result.documents ?? [],
        t,
      );
      onSaved({ ...result, documents: docs });
    } catch {
      toast(t('bookings.toast.saveFailed'), 'error');
    } finally {
      setBusy(false);
    }
  }

  if (files.extracting) return <ExtractingPlaceholder />;

  return (
    <form onSubmit={submit} className="space-y-3">
      <div>
        <label className={FIELD_LABEL}>{t('bookings.form.hotelName')}</label>
        <input
          className={'input' + err(submitted && !v.name.trim())}
          value={v.name}
          onChange={e => setV(x => ({ ...x, name: e.target.value }))}
          placeholder={t('bookings.form.hotelNamePlaceholder')}
        />
      </div>
      <div>
        <label className={FIELD_LABEL}>{t('bookings.form.address')}</label>
        <AddressAutocomplete
          value={v.address}
          onChange={val => setV(x => ({ ...x, address: val }))}
          onSelect={d => setV(x => ({ ...x, address: d.address || d.name }))}
          placeholder={t('bookings.form.searchPlace')}
        />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className={FIELD_LABEL}>{t('bookings.form.checkIn')}</label>
          <DatePicker withTime value={v.checkIn} onChange={d => setV(x => ({ ...x, checkIn: d }))} defaultMonth={tripStartDate} error={submitted && !v.checkIn} />
        </div>
        <div>
          <label className={FIELD_LABEL}>{t('bookings.form.checkOut')}</label>
          <DatePicker withTime value={v.checkOut} onChange={d => setV(x => ({ ...x, checkOut: d }))} defaultMonth={v.checkIn || tripStartDate} error={submitted && !v.checkOut} />
        </div>
      </div>

      <ReminderField value={v.reminderMinutes} onChange={m => setV(x => ({ ...x, reminderMinutes: m }))} />

      <div>
        <label className={FIELD_LABEL}>{t('bookings.form.notes')}</label>
        <textarea className="input" value={v.notes} onChange={e => setV(x => ({ ...x, notes: e.target.value }))} rows={2} />
      </div>

      <DocumentAttachments
        existing={existingDocs}
        onDeleteExisting={doc => removeExistingDoc(doc as HotelDocument)}
        docToDelete={docToDelete}
        onRequestDelete={doc => setDocToDelete(doc as HotelDocument | null)}
        staged={files.stagedFiles}
        onAddFiles={files.addFiles}
        onRemoveStaged={files.removeStaged}
        fileInputKey={files.fileInputKey}
        onExtract={files.runExtraction}
        extracting={files.extracting}
      />

      <div className="flex gap-2 pt-1">
        <button type="button" onClick={onCancel} className="flex-1 py-2 rounded-xl border border-slate-200 dark:border-slate-700 text-sm font-semibold text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer">{t('common.cancel')}</button>
        <Button type="submit" variant="cta" loading={busy} className="flex-1 justify-center py-2 text-sm disabled:opacity-50 disabled:pointer-events-none">
          {initial ? t('bookings.form.saveChanges') : t('bookings.form.addHotel')}
        </Button>
      </div>
    </form>
  );
}
